<template>
  <div class="calendar text-center">
    <TopMenu
      ref="topMenu"
      :title="$t('CALENDAR.PAGE_LABEL')"
      back-to="/dashboard"/>
    <div
      :style="'padding-top: ' + monthSelectorHeight + 'px;'"
      class="container-fluid content">
      <div class="fixed-month-selector">
        <MonthSelector
          ref="monthSelector"
          v-model="selectedMonth"/>
      </div>
      <div class="row mt-1 mr-2 ml-2">
        <table class="col-12">
          <tr>
            <td
              v-for="(h, i) in getFormattedCalendar(selectedMonth.year(), selectedMonth.month()).header"
              :key="i"
              class="calendar-cell-header row-item-with-border pt-1 pb-1">{{ h.charAt(0) }}</td>
          </tr>
          <tr
            v-for="(week, i) in getFormattedCalendar(selectedMonth.year(), selectedMonth.month()).weeks"
            :key="i">
            <td
              v-for="(day, j) in week"
              :key="j"
              :class="{'pt-2': i === 0, 'clickable-cell': day.date}"
              class="calendar-cell non-selectable-text"
              @click="dateOnClick(day)">
              <day
                :highlights-yellow="isToday(day.date)"
                :highlights-gray="isSelectedDate(day.date)"
                :show-black-dot="hasItems(day)"
                :show-red-dot="showTimeSheet && hasNoTimeEntered(day) && isEmployed"
                :current-date="day.date"/>
            </td>
          </tr>
        </table>
      </div>
      <detailed-day
        v-if="selectedDate"
        :current-date="selectedDate"
        :date-items="hasItems(selectedDateSummary) ? selectedDateSummary.dateSummary.calendarItems : []"
        :show-green-clock="showTimeSheet && hasTimeEntered(selectedDateSummary)"
        :show-red-clock="showTimeSheet && hasNoTimeEntered(selectedDateSummary) && isEmployed"
        class="mt-4 mb-3 mr-2 ml-2"/>
    </div>
    <BottomMenu />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import moment from 'moment';
import TopMenu from '@/components/Menu/TopMenu';
import BottomMenu from '@/components/Menu/BottomMenu';
import TextLink from '@/components/Utils/TextLink';
import Day from '@/components/Date/Calendar/Day';
import DetailedDay from '@/components/Date/Calendar/DetailedDay';
import MonthSelector from '@/components/Date/MonthSelector';

export default {
  name: 'Calendar',
  components: {
    TextLink,
    TopMenu,
    BottomMenu,
    Day,
    DetailedDay,
    MonthSelector,
  },
  data() {
    return {
      monthSelectorHeight: null,
      selectedMonth: moment({
        y: moment().year(),
        M: moment().month(),
        d: 1,
        h: 0,
        m: 0,
        s: 0,
        ms: 0,
      }),
      selectedDate: moment(),
    };
  },
  computed: {
    ...mapState('calendar', ['calendarDates']),
    ...mapState('member', ['selectedCalendarPreferences', 'selectedEmployment']),
    ...mapGetters('calendar', [
      'getFormattedCalendar',
      'getCalendarDateSummary',
      'isPastDay',
      'isPastWeek',
      'isWorkDay',
      'hasItems',
      'hasTimeEntered',
      'hasTimeNotSubmitted',
      'hasNoTimeEntered',
    ]),
    showTimeSheet() {
      let show = true;
      if (this.selectedCalendarPreferences) {
        this.selectedCalendarPreferences.forEach((pref) => {
          if (pref.name === 'CALENDAR_DISPLAY_SETTINGS.TIME_SHEET') {
            show = pref.value;
          }
        });
      }
      return show;
    },
    selectedDateSummary() {
      return this.getCalendarDateSummary(this.selectedDate);
    },
    firstDay() {
      return this.selectedMonth;
    },
    lastDay() {
      if (this.selectedMonth) {
        return moment(this.selectedMonth).add(
          this.selectedMonth.daysInMonth() - 1,
          'day',
        );
      }
      return this.firstDay;
    },
    isEmployed() {
      return (this.selectedEmployment && this.selectedEmployment.hasJob);
    },
  },
  watch: {
    selectedMonth() {
      this.$store.dispatch('calendar/getCalendarSummary', {
        startDate: this.firstDay.format('YYYY-MM-DD'),
        endDate: this.lastDay.format('YYYY-MM-DD'),
      });
      if (this.selectedMonth.format('YYYY-MM') === moment().format('YYYY-MM')) {
        this.selectedDate = moment();
      } else {
        this.selectedDate = null;
      }
    },
  },
  created() {
    this.updateRender();
    this.$store.dispatch('calendar/getCalendarSummary', {
      startDate: this.firstDay.format('YYYY-MM-DD'),
      endDate: this.lastDay.format('YYYY-MM-DD'),
    });
    this.$store.dispatch('member/currentMemberCalendarPreferences');
    this.$store.dispatch('member/currentMemberEmployment');
  },
  mounted() {
    this.updateRender();
  },
  updated() {
    this.updateRender();
  },
  methods: {
    updateRender() {
      if (this.$refs.monthSelector && this.$refs.topMenu) {
        this.monthSelectorHeight = this.$refs.monthSelector.$el.offsetHeight
          + this.$refs.topMenu.$el.offsetHeight;
      }
    },
    dateOnClick(day) {
      if (day.date) this.selectedDate = day.date;
    },
    isSelectedDate(d) {
      let isSelected = false;
      if (this.selectedDate && d) {
        isSelected = d.format('YYYY-MM-DD') === this.selectedDate.format('YYYY-MM-DD');
      }
      return isSelected;
    },
    isToday(d) {
      let isToday = false;
      if (d) {
        isToday = d.format('YYYY-MM-DD') === moment().format('YYYY-MM-DD');
      }
      return isToday;
    },
  },
};
</script>

<style scoped lang="scss">
div {
  &.calendar {
    height: 100%;
  }
  &.content {
    padding-bottom: $bottom-menu-height;
    background-color: $base;
    min-height: 100%;
  }
  &.fixed-month-selector {
    position: fixed;
    width: 100%;
    z-index: 1001;
    left: 0;
    top: $top-menu-height;
    background-color: $primary;
    border-bottom: 2px solid $primary;
  }
}
.calendar-cell {
  width: 14.3%;
}
.calendar-cell-header {
  width: 14.3%;
  text-transform: uppercase;
}
.clickable-cell {
  cursor: pointer;
}
</style>
